import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { 
  ArrowLeft, Copy, DollarSign, Users, TrendingUp, 
  Wallet, CheckCircle, Clock, Share2, ExternalLink,
  ArrowUpRight, ArrowDownRight, RefreshCw, Gift,
  Smartphone, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

export default function PartnerDashboard() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutPhone, setPayoutPhone] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('jambo_token');
      const response = await axios.get(`${API_URL}/api/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      console.error('Dashboard error:', error);
      toast.error('Failed to load partner dashboard');
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}?ref=${dashboardData?.partner?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard!');
  };

  const copyReferralCode = () => {
    navigator.clipboard.writeText(dashboardData?.partner?.referral_code);
    toast.success('Referral code copied!');
  };

  const shareReferral = async () => {
    const link = `${window.location.origin}?ref=${dashboardData?.partner?.referral_code}`;
    const text = `Shop on JamboBuy and get amazing deals! Use my referral code: ${dashboardData?.partner?.referral_code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({ title: 'JamboBuy Referral', text, url: link });
      } catch (err) {
        copyReferralLink();
      }
    } else {
      copyReferralLink();
    }
  };

  const requestPayout = async () => {
    const amount = parseFloat(payoutAmount);
    if (!amount || amount < 1000) {
      toast.error('Minimum payout is KES 1,000');
      return;
    }
    if (amount > (dashboardData?.stats?.pending_payout || 0)) {
      toast.error('Amount exceeds available balance');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('jambo_token');
      await axios.post(`${API_URL}/api/partner/request-payout`, {
        amount,
        payment_method: 'mpesa',
        phone_number: payoutPhone || user?.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Payout of ${formatCurrency(amount)} processed!`);
      setShowPayoutModal(false);
      setPayoutAmount('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout failed');
    } finally {
      setProcessing(false);
    }
  };

  // Simulate adding referral earnings for demo
  const simulateReferral = async () => {
    try {
      await axios.post(`${API_URL}/api/partner/track-referral?referral_code=${dashboardData?.partner?.referral_code}&order_amount=5000`);
      toast.success('Demo referral tracked! +KES 500 commission');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to track referral');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  // Not a partner yet
  if (dashboardData?.status === 'not_partner') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
              <Gift className="h-8 w-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Become a Partner</h2>
            <p className="text-muted-foreground mb-6">
              Join Jambo Capital and start earning by referring customers, reselling products, or providing services.
            </p>
            <Link to="/">
              <Button className="jambo-gradient text-white w-full">
                Explore Jambo Capital Programs
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Application pending
  if (dashboardData?.status === 'pending') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Application Under Review</h2>
            <p className="text-muted-foreground mb-4">
              Your application for <strong>{dashboardData?.application?.program_name}</strong> is being reviewed. 
              We'll notify you once approved.
            </p>
            <Badge variant="outline" className="text-blue-600 border-blue-600">
              Submitted: {new Date(dashboardData?.application?.created_at).toLocaleDateString()}
            </Badge>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { partner, stats, recent_referrals, payout_history } = dashboardData;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-bold">Partner Dashboard</h1>
              <p className="text-sm text-muted-foreground">{partner?.program_name}</p>
            </div>
          </div>
          <Badge className="bg-green-500 text-white">
            <CheckCircle className="h-3 w-3 mr-1" /> Active Partner
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Referral Card */}
        <Card className="mb-8 overflow-hidden">
          <div className="jambo-gradient p-6 text-white">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <p className="text-white/80 text-sm mb-1">Your Referral Code</p>
                <div className="flex items-center gap-3">
                  <span className="text-3xl font-bold tracking-wider">{partner?.referral_code}</span>
                  <button 
                    onClick={copyReferralCode}
                    className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition"
                  >
                    <Copy className="h-5 w-5" />
                  </button>
                </div>
                <p className="text-white/80 text-sm mt-2">Commission Rate: {partner?.commission_rate}</p>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="secondary" 
                  className="bg-white text-cyan-600 hover:bg-white/90"
                  onClick={copyReferralLink}
                >
                  <Copy className="h-4 w-4 mr-2" /> Copy Link
                </Button>
                <Button 
                  variant="secondary"
                  className="bg-white/20 text-white hover:bg-white/30 border-0"
                  onClick={shareReferral}
                >
                  <Share2 className="h-4 w-4 mr-2" /> Share
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_referrals || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Referrals</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.successful_referrals || 0}</p>
                  <p className="text-xs text-muted-foreground">Successful</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <TrendingUp className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.conversion_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Conversion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{formatCurrency(stats?.total_earnings || 0)}</p>
                  <p className="text-xs text-muted-foreground">Total Earned</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Earnings & Payout */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Available Balance */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Available Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between mb-6">
                <div>
                  <p className="text-4xl font-bold jambo-gradient-text">
                    {formatCurrency(stats?.pending_payout || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Ready for withdrawal</p>
                </div>
                <Button 
                  className="jambo-gradient text-white"
                  onClick={() => setShowPayoutModal(true)}
                  disabled={!stats?.pending_payout || stats.pending_payout < 1000}
                >
                  <Wallet className="h-4 w-4 mr-2" /> Withdraw
                </Button>
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <p className="text-sm text-muted-foreground">Total Paid</p>
                  <p className="font-semibold flex items-center gap-1">
                    <ArrowUpRight className="h-4 w-4 text-green-500" />
                    {formatCurrency(stats?.total_paid || 0)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">This Month</p>
                  <p className="font-semibold flex items-center gap-1">
                    <TrendingUp className="h-4 w-4 text-blue-500" />
                    {formatCurrency(stats?.this_month_earnings || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={copyReferralLink}
              >
                <Copy className="h-4 w-4 mr-3" /> Copy Referral Link
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={shareReferral}
              >
                <Share2 className="h-4 w-4 mr-3" /> Share on Social Media
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start text-amber-600 border-amber-300 hover:bg-amber-50"
                onClick={simulateReferral}
              >
                <Gift className="h-4 w-4 mr-3" /> Demo: Simulate Referral (+KES 500)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={fetchDashboard}
              >
                <RefreshCw className="h-4 w-4 mr-3" /> Refresh Stats
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Recent Referrals */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              {(!recent_referrals || recent_referrals.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No referrals yet</p>
                  <p className="text-sm">Share your link to start earning!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {recent_referrals.map((ref, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          ref.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {ref.status === 'completed' ? <CheckCircle className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
                        </div>
                        <div>
                          <p className="text-sm font-medium">
                            {ref.status === 'completed' ? 'Order Completed' : 'Link Clicked'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(ref.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-green-600">+{formatCurrency(ref.commission || 0)}</p>
                        <p className="text-xs text-muted-foreground">Order: {formatCurrency(ref.order_amount || 0)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payout History */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Payout History</CardTitle>
            </CardHeader>
            <CardContent>
              {(!payout_history || payout_history.length === 0) ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No payouts yet</p>
                  <p className="text-sm">Earn KES 1,000+ to request payout</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payout_history.map((payout, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          payout.status === 'completed' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'
                        }`}>
                          <Smartphone className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">M-Pesa</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(payout.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(payout.amount)}</p>
                        <Badge variant={payout.status === 'completed' ? 'default' : 'outline'} className="text-xs">
                          {payout.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Payout Modal */}
      <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Request Payout</DialogTitle>
            <DialogDescription>
              Withdraw your earnings to M-Pesa. Minimum: KES 1,000
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 text-center">
              <p className="text-sm text-muted-foreground">Available Balance</p>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(stats?.pending_payout || 0)}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="amount">Amount (KES)</Label>
              <Input 
                id="amount"
                type="number"
                value={payoutAmount}
                onChange={(e) => setPayoutAmount(e.target.value)}
                placeholder="Enter amount"
                min={1000}
                max={stats?.pending_payout || 0}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">M-Pesa Number</Label>
              <Input 
                id="phone"
                value={payoutPhone}
                onChange={(e) => setPayoutPhone(e.target.value)}
                placeholder="+254 7XX XXX XXX"
              />
              <p className="text-xs text-muted-foreground">Leave blank to use registered number</p>
            </div>
            
            <div className="flex items-center gap-2 p-3 rounded-lg bg-muted">
              <Smartphone className="h-5 w-5 text-green-600" />
              <span className="text-sm">Payment via M-Pesa (Instant)</span>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowPayoutModal(false)}>
              Cancel
            </Button>
            <Button 
              className="flex-1 jambo-gradient text-white"
              onClick={requestPayout}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Withdraw'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
